import React, { useState } from 'react';

const EXAMS = ['FORMATIVE ASSESSMENT -1', 'FORMATIVE ASSESSMENT -2', 'SUMMATIVE ASSESSMENT-1'];

const CLASS_SECTION_MAP = {
  'PLAY GROUP': ['A'],
  'NURSERY':    ['A', 'B'],
  'PP-1':       ['A'],
  '1ST CLASS':  ['A'],
};

const SUBJECTS_BY_CLASS = {
  'PLAY GROUP': ['ENGLISH', 'MATHS', 'EVS', 'RHYMES & STORY TELLING', 'ART & CRAFT \\ ORAL'],
  'NURSERY':    ['ENGLISH', 'MATHS', 'EVS', 'RHYMES & STORY TELLING', 'ART & CRAFT \\ ORAL'],
  'PP-1':       ['ENGLISH', 'MATHS', 'EVS', 'HINDI', 'RHYMES & STORY TELLING', 'ART & CRAFT \\ ORAL', 'TELUGU'],
  '1ST CLASS':  ['ENGLISH', 'MATHS', 'EVS', 'HINDI', 'RHYMES & STORY TELLING', 'ART & CRAFT \\ ORAL', 'TELUGU'],
};

function buildInitialRows(cls) {
  return (SUBJECTS_BY_CLASS[cls] || []).map((name, idx) => ({
    name,
    checked:      false, // always start unchecked
    date:         '',
    time:         '',
    minMarks:     '',
    maxMarks:     '',
    seq:          idx + 1,
    otherSubject: false,
    subSubject:   false,
  }));
}

/* ── Chip: checkmark span always in DOM, toggled via visibility ── */
function Chip({ label, selected, onClick, small }) {
  return (
    <span
      onClick={onClick}
      style={{
        cursor:      'pointer',
        userSelect:  'none',
        display:     'inline-flex',
        alignItems:  'center',
        gap:         4,
        padding:     small ? '4px 12px' : '7px 18px',
        borderRadius: 20,
        fontSize:    small ? 12 : 13,
        fontWeight:  600,
        transition:  'all 0.15s',
        border:      selected ? '2px solid #0d6efd' : '2px solid #dee2e6',
        background:  selected ? '#0d6efd'           : '#fff',
        color:       selected ? '#fff'              : '#495057',
        boxShadow:   selected ? '0 2px 6px rgba(13,110,253,.25)' : 'none',
      }}
    >
      {/* Always rendered — visibility controls presence, no mount/unmount */}
      <span style={{
        fontSize:   10,
        visibility: selected ? 'visible' : 'hidden',
        width:      10,
        display:    'inline-block',
        flexShrink: 0,
      }}>✔</span>
      {label}
    </span>
  );
}

/* ── MiniToggle: inner text always rendered ── */
function MiniToggle({ checked, onChange }) {
  return (
    <span
      onClick={() => onChange(!checked)}
      style={{
        display:        'inline-flex',
        alignItems:     'center',
        justifyContent: 'center',
        width:          22,
        height:         22,
        borderRadius:   '50%',
        cursor:         'pointer',
        fontSize:       12,
        fontWeight:     700,
        border:         checked ? 'none' : '1px solid #ced4da',
        background:     checked ? '#198754' : '#f8f9fa',
        color:          checked ? '#fff'    : 'transparent', // text always present
        userSelect:     'none',
        transition:     'all 0.15s',
      }}
    >
      ✓
    </span>
  );
}

/* ── Subject table for one class+section ── */
function SubjectTable({ cls, sec, rows, onUpdateRow, onCopy, showCopyButton }) {
  return (
    <div className="mb-3" style={{ marginLeft: 24 }}>
      {/* Header row */}
      <div className="d-flex align-items-center justify-content-between mb-2">
        <span style={{ fontWeight: 600, fontSize: 13, color: '#1565c0', background: '#e3f2fd', border: '1px solid #90caf9', borderRadius: 12, padding: '3px 12px' }}>
          Section {sec}
        </span>
        {showCopyButton && (
          <button type="button" className="btn btn-sm" onClick={onCopy}
            style={{ background: '#15a3a8', color: '#fff', border: 'none', fontSize: 12 }}>
            🗐 Copy to other sections
          </button>
        )}
      </div>

      <div className="table-responsive pop-tbl">
        <table className="table table-bordered mb-0" style={{ fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f1f3f5' }}>
              <th>Subject</th>
              <th style={{ width: 44, textAlign: 'center' }}>✓</th>
              <th style={{ width: 145 }}>Date</th>
              <th style={{ width: 180 }}>Time</th>
              <th style={{ width: 90 }}>Min Marks</th>
              <th style={{ width: 90 }}>Max Marks</th>
              <th style={{ width: 58 }}>Seq</th>
              <th style={{ width: 90, textAlign: 'center' }}>Other Subj.</th>
              <th style={{ width: 90, textAlign: 'center' }}>Sub Subj.</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={row.name} style={{ background: row.checked ? '#f0fff4' : '#fff', transition: 'background 0.15s' }}>
                <td className='fw-semibold ps-2'>{row.name}</td>
                <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                  <MiniToggle checked={row.checked} onChange={v => onUpdateRow(idx, 'checked', v)} />
                </td>
                <td style={{ verticalAlign: 'middle' }}>
                  <input type="date" className="form-control form-control-sm"
                    value={row.date} onChange={e => onUpdateRow(idx, 'date', e.target.value)} />
                </td>
                <td style={{ verticalAlign: 'middle' }}>
                  <input type="text" className="form-control form-control-sm"
                    placeholder="e.g. 11:00 AM - 11:45 AM"
                    value={row.time} onChange={e => onUpdateRow(idx, 'time', e.target.value)} />
                </td>
                <td style={{ verticalAlign: 'middle' }}>
                  <input type="number" className="form-control form-control-sm"
                    value={row.minMarks} onChange={e => onUpdateRow(idx, 'minMarks', e.target.value)} />
                </td>
                <td style={{ verticalAlign: 'middle' }}>
                  <input type="number" className="form-control form-control-sm"
                    value={row.maxMarks} onChange={e => onUpdateRow(idx, 'maxMarks', e.target.value)} />
                </td>
                <td style={{ verticalAlign: 'middle' }}>
                  <input type="number" className="form-control form-control-sm"
                    value={row.seq} onChange={e => onUpdateRow(idx, 'seq', e.target.value)} />
                </td>
                <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                  <MiniToggle checked={row.otherSubject} onChange={v => onUpdateRow(idx, 'otherSubject', v)} />
                </td>
                <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                  <MiniToggle checked={row.subSubject} onChange={v => onUpdateRow(idx, 'subSubject', v)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════ */
export default function ExamSubjectSetup() {
  const [selectedExam,    setSelectedExam]    = useState(EXAMS[0]);
  const [selectedClasses, setSelectedClasses] = useState([]);          // nothing selected initially
  const [selectedSectionsByClass, setSelectedSectionsByClass] = useState({});
  const [tableData, setTableData] = useState({});

  /* Ensure rows exist for cls+sec before showing the table */
  const ensureRows = (cls, sec) => {
    const key = `${cls}|${sec}`;
    setTableData(prev => prev[key] ? prev : { ...prev, [key]: buildInitialRows(cls) });
  };

  /* Toggle a class chip */
  const toggleClass = (cls) => {
    setSelectedClasses(prev => {
      if (prev.includes(cls)) {
        // Deselect class — also clear its section selections
        setSelectedSectionsByClass(p => { const n = { ...p }; delete n[cls]; return n; });
        return prev.filter(c => c !== cls);
      }
      return [...prev, cls];
    });
  };

  /* Toggle a section chip under a class */
  const toggleSection = (cls, sec) => {
    setSelectedSectionsByClass(prev => {
      const current = prev[cls] || [];
      const next = current.includes(sec)
        ? current.filter(s => s !== sec)
        : [...current, sec];
      return { ...prev, [cls]: next };
    });
    ensureRows(cls, sec);
  };

  /* Update a single cell in a subject table */
  const updateRow = (cls, sec, idx, field, value) => {
    const key = `${cls}|${sec}`;
    setTableData(prev => {
      const rows = [...(prev[key] || [])];
      rows[idx] = { ...rows[idx], [field]: value };
      return { ...prev, [key]: rows };
    });
  };

  /* Copy one section's table to all other sections of the same class */
  const copyToOtherSections = (cls, fromSec) => {
    const allSecs = CLASS_SECTION_MAP[cls] || [];
    const sourceRows = tableData[`${cls}|${fromSec}`] || [];
    setTableData(prev => {
      const next = { ...prev };
      allSecs.forEach(sec => {
        if (sec === fromSec) return;
        next[`${cls}|${sec}`] = sourceRows.map(r => ({ ...r }));
      });
      return next;
    });
    setSelectedSectionsByClass(prev => ({ ...prev, [cls]: allSecs }));
  };

  return (
    <div className="container py-4" style={{ maxWidth: 1100 }}>
      <style>{`
        .mini-toggle:hover { opacity: .8; }
        .cls-sec-panel { background:#fff; border-radius:8px; box-shadow:0 1px 4px rgba(0,0,0,.08); padding:20px; }
      `}</style>

      {/* Exam selector */}
      {/* <div className="mb-4">
        <label className="form-label fw-semibold">Select Exam</label>
        <select className="form-select" style={{ maxWidth: 400 }}
          value={selectedExam} onChange={e => setSelectedExam(e.target.value)}>
          {EXAMS.map(ex => <option key={ex} value={ex}>{ex}</option>)}
        </select>
      </div> */}

      {/* Class + section pickers */}
      <div className="cls-sec-panel mb-4">
        <label className="form-label fw-semibold d-block mb-3">Select Class &amp; Section</label>

        {Object.keys(CLASS_SECTION_MAP).map(cls => {
          const classSelected  = selectedClasses.includes(cls);
          const secsForClass   = CLASS_SECTION_MAP[cls];
          const selectedSecs   = selectedSectionsByClass[cls] || [];

          return (
            <div key={cls} style={{ marginBottom: 20 }}>

              {/* Class chip */}
              <Chip
                label={cls}
                selected={classSelected}
                onClick={() => toggleClass(cls)}
              />

              {/* Section chips — always in DOM but hidden via CSS height/opacity */}
              <div style={{
                marginTop:    classSelected ? 10 : 0,
                marginLeft:   20,
                display:      'flex',
                flexWrap:     'wrap',
                gap:          8,
                overflow:     'hidden',
                maxHeight:    classSelected ? 60 : 0,
                opacity:      classSelected ? 1  : 0,
                transition:   'max-height 0.2s ease, opacity 0.2s ease',
              }}>
                {secsForClass.map(sec => (
                  <Chip
                    key={sec}
                    label={`Section ${sec}`}
                    small
                    selected={selectedSecs.includes(sec)}
                    onClick={() => classSelected && toggleSection(cls, sec)}
                  />
                ))}
              </div>

              {/* Subject tables — one per selected section */}
              <div style={{ marginTop: selectedSecs.length > 0 ? 14 : 0 }}>
                {secsForClass.map(sec => {
                  const show = classSelected && selectedSecs.includes(sec);
                  return (
                    <div
                      key={`${cls}-${sec}`}
                      style={{
                        overflow:   'hidden',
                        maxHeight:  show ? 2000 : 0,
                        opacity:    show ? 1    : 0,
                        transition: 'max-height 0.3s ease, opacity 0.25s ease',
                      }}
                    >
                      <SubjectTable
                        cls={cls}
                        sec={sec}
                        rows={tableData[`${cls}|${sec}`] || buildInitialRows(cls)}
                        onUpdateRow={(idx, field, value) => updateRow(cls, sec, idx, field, value)}
                        onCopy={() => copyToOtherSections(cls, sec)}
                        showCopyButton={secsForClass.length > 1}
                      />
                    </div>
                  );
                })}
              </div>

            </div>
          );
        })}
      </div>
    </div>
  );
}